import projectList from "./projectList";
import { renderPage } from "./renderPage";
import { createProjectElement } from "./projectDOM";
import { renderCalendar } from "./calendarDOM";
import {
  addToStorage,
  getFromStorage,
  updateStorage,
  deleteFromStorage,
} from "./storage";

// ====================== HELPERS ====================== //
function activateSidebarItem(sidebarItem, pageName = null) {
  if (pageName) {
    sidebarItem = Array.from(document.querySelectorAll(".sidebar-item")).find(
      (item) =>
        item.querySelector(".sidebar-link span")?.textContent.trim() ===
        pageName
    );
    if (!sidebarItem) return;
  }

  const linkText = sidebarItem
    .querySelector(".sidebar-link span")
    .textContent.trim();

  const activePage = document.querySelector(".sidebar .active");
  if (activePage) activePage.classList.remove("active");
  sidebarItem.classList.add("active");

  const isProject = isProjectPage(linkText);
  renderPage(linkText, isProject);
}

function goToPage(pageName) {
  activateSidebarItem(null, pageName);
}

// specifically handle when page clicked from sidebar
function selectPageFromSidebar(target) {
  const sidebarItem = target.closest(".sidebar-item");
  if (!sidebarItem || target.closest(".sidebar-item--header")) return;
  activateSidebarItem(sidebarItem);
}

function getActivePage() {
  return document.querySelector(".sidebar .active span").textContent;
}

function getActiveProject() {
  return projectList.getProject(getActivePage());
}

function isProjectPage(activePage) {
  const TASK_FILTERS = new Set([
    "All",
    "Today",
    "Upcoming",
    "Important",
    "Completed",
  ]);
  return !TASK_FILTERS.has(activePage);
}

// ====================== MAIN HANDLERS ====================== //
function handleScreenResize() {
  if (document.querySelector(".sidebar").classList.contains("open")) {
    document.querySelector(".sidebar").classList.remove("open");
  }
}

function toggleSidebar() {
  const body = document.body;
  const mql = matchMedia("(max-width: 768px)");

  if (mql.matches) {
    // within the width should not change grid-tcs
    document.querySelector(".sidebar").classList.toggle("open");
    body.classList.remove("collapsed");
    document.querySelector(".sidebar").classList.remove("collapsed");
  } else {
    // outside width should change grid-tcs
    body.classList.toggle("collapsed");
    document.querySelector(".sidebar").classList.toggle("collapsed");
  }
}

function openTaskForm(target, option) {
  handleScreenResize();

  const taskDialog = document.querySelector("#tasks");
  const formTitle = taskDialog.querySelector(".form-title");
  const formDescription = taskDialog.querySelector(".form-description");
  const selectPriorityButton = taskDialog.querySelector(".select-priority");
  const dropdownList = taskDialog.querySelector(".dropdown-list");
  const selectDateButton = taskDialog.querySelector(".select-date");
  const calendar = taskDialog.querySelector(".calendar");
  const cancelButton = taskDialog.querySelector(".cancel");
  const submitButton = taskDialog.querySelector(".add");
  submitButton.textContent = option;

  const resetForm = () => {
    submitButton.textContent = option;
    selectDateButton.textContent = "Select date";
    selectPriorityButton.textContent = "Select priority";
    renderCalendar(new Date().getFullYear(), new Date().getMonth());
    taskDialog.querySelector("form").reset();
    taskDialog.querySelector(".form-submit span").textContent = "";
    taskDialog.close();
    detachListeners();
  };

  const populateForm = () => {
    if (option !== "Update") return;

    const taskItem = target.closest(".task-item");
    const { task, project } = projectList.findTaskInProjects(taskItem.id);
    formTitle.value = task.getTitle();
    formDescription.value = task.getDescription();

    const date = task.getDueDate();
    selectDateButton.textContent = `${
      date.getMonth() + 1
    }/${date.getDate()}/${date.getFullYear()}`;

    selectPriorityButton.textContent = task.getPriority();
  };

  const handleSubmit = () => {
    if (
      formTitle.value.trim() === "" ||
      formTitle.value.length > 45 ||
      selectDateButton.textContent === "Select date" ||
      selectPriorityButton.textContent === "Select priority"
    )
      return;
    const dueDate = new Date(selectDateButton.textContent);
    const priority = selectPriorityButton.textContent;

    if (
      projectList.taskExists(
        formTitle.value,
        formDescription.value,
        dueDate.getTime(),
        priority
      )
    ) {
      taskDialog.querySelector(".form-submit span").textContent =
        "Cannot create duplicate tasks.";
      return;
    }

    if (option === "Create") {
      const project = getActiveProject();
      const task = project.addTask(
        formTitle.value,
        formDescription.value,
        dueDate,
        priority,
        false
      );
      addToStorage({
        type: "task",
        projectName: project.getProjectTitle(),
        task: task,
      });
    }

    if (option === "Update") {
      const taskItem = target.closest(".task-item");
      const { task, project } = projectList.findTaskInProjects(taskItem.id);

      task.setTitle(formTitle.value);
      task.setDescription(formDescription.value);
      task.setDueDate(dueDate);
      task.setPriority(priority);

      updateStorage({
        type: "task",
        oldProjectName: project.getProjectTitle(),
        newProjectName: null,
        task: task,
      });
    }

    resetForm();
    renderPage(getActivePage(), isProjectPage(getActivePage()));
  };

  const handleSelectDate = () => {
    calendar.classList.toggle("hide");
  };

  const handleSelectPriority = () => {
    dropdownList.classList.toggle("hide");
  };

  const handleCalendar = (event) => {
    if (event.target.matches(".date")) {
      calendar.classList.toggle("hide");
      selectDateButton.textContent = event.target.id;
    }
  };

  const handleDropdownList = (event) => {
    if (event.target.matches(".dropdown-option")) {
      selectPriorityButton.textContent = event.target.textContent;
      dropdownList.classList.toggle("hide");
    }
  };

  const attachListeners = () => {
    selectDateButton.addEventListener("click", handleSelectDate);
    selectPriorityButton.addEventListener("click", handleSelectPriority);
    calendar.addEventListener("click", handleCalendar);
    dropdownList.addEventListener("click", handleDropdownList);
    submitButton.addEventListener("click", handleSubmit);
  };

  const detachListeners = () => {
    selectDateButton.removeEventListener("click", handleSelectDate);
    selectPriorityButton.removeEventListener("click", handleSelectPriority);
    calendar.removeEventListener("click", handleCalendar);
    dropdownList.removeEventListener("click", handleDropdownList);
    submitButton.removeEventListener("click", handleSubmit);
  };

  // handle form escapes
  cancelButton.addEventListener(
    "click",
    () => {
      if (option === "Update") resetForm();
      taskDialog.close();
      detachListeners();
    },
    { once: true }
  );
  document.addEventListener(
    "keypress",
    (event) => {
      if (event.key === "Escape") {
        detachDialogListeners();
        if (option === "Update") resetForm();
      }
    },
    { once: true }
  );

  // collapse when clicking away
  taskDialog.addEventListener("click", (event) => {
    if (
      !calendar.classList.contains("hide") &&
      !event.target.closest(".calendar") &&
      !event.target.matches(".select-date")
    ) {
      calendar.classList.toggle("hide");
    }
    if (
      !dropdownList.classList.contains("hide") &&
      !event.target.closest(".dropdown-list") &&
      !event.target.matches(".select-priority")
    ) {
      dropdownList.classList.toggle("hide");
    }
  });

  populateForm();
  attachListeners();
  taskDialog.showModal();
}

function handleTaskUpdates(target) {
  const taskItem = target.closest(".task-item");
  if (!taskItem) return false;

  const { task, project } = projectList.findTaskInProjects(taskItem.id);

  if (target.matches(".task-checkbox")) {
    target.classList.toggle("complete");
    task.setCompletedStatus(target.classList.contains("complete"));

    updateStorage({
      type: "task",
      oldProjectName: project.getProjectTitle(),
      newProjectName: null,
      task: task,
    });

    renderPage(getActivePage(), isProjectPage(getActivePage()));
    return true;
  }

  if (target.closest(".delete")) {
    project.removeTask(task);

    deleteFromStorage({
      type: "task",
      projectName: project.getProjectTitle(),
      task: task,
    });

    renderPage(getActivePage(), isProjectPage(getActivePage()));
    return true;
  }

  return false;
}

function openProjectForm(target, option, project = null) {
  handleScreenResize();

  const projectDialog = document.querySelector("#projects");
  const cancelButton = projectDialog.querySelector(".cancel");
  const submitButton = projectDialog.querySelector(".add");
  submitButton.textContent = option;

  const title = projectDialog.querySelector(".form-title");
  const errorBox = document.querySelector(".error-box");
  const projectItem = target.closest(".project-item");

  function resetDialog() {
    projectDialog.close();
    title.classList.remove("invalid");
    errorBox.style.opacity = 0;
    projectDialog.querySelector("form").reset();
    submitButton.removeEventListener("click", handleSubmit);
  }

  function handleSubmit() {
    if (title.classList.contains("invalid") || title.value.trim() === "")
      return;

    const projectTitle = title.value.trim();

    if (option === "Create") {
      const projectExists = projectList
        .getProjectList()
        .some((proj) => proj.getProjectTitle() === projectTitle);

      if (projectExists) {
        document.querySelector(".error-box").style.opacity = 1;
        return;
      }

      projectList.addProject(projectTitle);
      createProjectElement(projectTitle);
      addToStorage({ type: "project", projectName: projectTitle, task: null });
    }

    if (option === "Update" && project) {
      const oldTitle = project.getProjectTitle();
      project.renameProject(projectTitle);
      projectItem.querySelector("span").textContent = projectTitle;

      updateStorage({
        type: "project",
        oldProjectName: oldTitle,
        newProjectName: projectTitle,
        task: null,
      });
    }

    resetDialog();
    goToPage(projectTitle);
  }

  submitButton.addEventListener("click", handleSubmit);

  cancelButton.addEventListener("click", () => resetDialog(), { once: true });

  document.addEventListener(
    "keypress",
    (event) => {
      if (event.key === "Escape") resetDialog();
    },
    { once: true }
  );

  title.addEventListener("blur", () => {
    if (title.value.trim() === "" || title.value.length > 45) {
      title.classList.add("invalid");
      errorBox.style.opacity = 1;
    } else {
      title.classList.remove("invalid");
      errorBox.style.opacity = 0;
    }
  });

  if (option === "Update" && project) {
    title.value = project.getProjectTitle();
  }

  projectDialog.showModal();
}

function handleProjectUpdates(target) {
  const projectItem = target.closest(".project-item");
  const projectTitle = projectItem.querySelector("span").textContent;
  const project = projectList.getProject(projectTitle);

  if (target.closest(".edit")) {
    openProjectForm(target, "Update", project);
  } else if (target.closest(".delete")) {
    projectList.removeProject(project);
    projectItem.remove();

    deleteFromStorage({
      type: "project",
      projectName: projectTitle,
      task: null,
    });

    goToPage("Home");
  }
}

function initializeFromStorage() {
  const data = getFromStorage();
  const projectNames = data.projects;

  projectNames.forEach((projectName) => {
    projectList.addProject(projectName);
    createProjectElement(projectName);
  });

  projectNames.forEach((projectName) => {
    const project = projectList.getProject(projectName);
    const tasks = data[projectName];
    if (tasks) {
      tasks.forEach((task) => {
        project.addExistingTask(task);
      });
    }
  });
}

function displayWebsite() {
  const sidebar = document.querySelector(".sidebar");

  initializeFromStorage();
  goToPage("Home");

  window.addEventListener("resize", () => handleScreenResize(sidebar));

  document.addEventListener("click", (event) => {
    const target = event.target;
    const isSidebarToggle = target.closest(".sidebar-toggle");
    const isAddTaskButton = target.closest(".view-add-tasks");
    const isAddProjectButton = target.closest("#add-project");
    const isTaskItem = target.closest(".task-item");
    const isFilterPage =
      target.closest(".sidebar-item") && !target.closest(".project-item");
    const isProjectItem = target.closest(".project-item");
    const isOutsideSidebar =
      !target.closest(".sidebar") && sidebar.classList.contains("open");

    if (isSidebarToggle) {
      toggleSidebar();
    } else if (isAddTaskButton) {
      openTaskForm(target, "Create");
    } else if (isAddProjectButton) {
      openProjectForm(target, "Create", null);
    } else if (isTaskItem) {
      if (target.matches(".task-checkbox") || target.closest(".delete")) {
        handleTaskUpdates(target);
      } else {
        openTaskForm(target, "Update");
      }
    } else if (isFilterPage) {
      selectPageFromSidebar(target);
    } else if (isProjectItem) {
      if (!target.closest(".edit") && !target.closest(".delete")) {
        selectPageFromSidebar(target);
      } else {
        handleProjectUpdates(target);
      }
    } else if (isOutsideSidebar) {
      sidebar.classList.remove("open");
    }
  });
}

export { displayWebsite };
